import { ref } from 'vue';
import { authenticatedFetch } from '@/utils/apiHelper';

/**
 * Composable for managing equipment documentation dialogs and statuses
 *
 * Handles:
 * - Documentation dialog state (both old and new category-based systems)
 * - Documentation status tracking and badges
 * - Navigation between documentation and airflow tabs
 * - API integration for documentation stats
 *
 * @param {Object} config Configuration options
 * @param {number|string} config.siteId - The site ID for API calls
 * @param {Function} config.onNavigate - Callback for tab/state navigation (receives { tab, equipment, ahuId, ahuName })
 * @param {Function} config.notifySuccess - Callback for success notifications
 * @param {Function} config.notifyError - Callback for error notifications
 * @param {Function} config.findAhuNode - Function to find AHU node by ID (from parent)
 * @returns {Object} Documentation dialog state and methods
 */
export function useDocumentationDialog({
  siteId,
  onNavigate,
  notifySuccess,
  notifyError,
  findAhuNode,
}) {
  // State for old documentation system (backward compatibility)
  const documentationDialog = ref(false);
  const documentationEquipment = ref(null);

  // State for new category-based documentation system
  const categoryDocumentationDialog = ref(false);
  const categoryDocumentationEquipment = ref(null);

  // Documentation status tracking
  const documentationStatuses = ref({});
  const documentationRefreshTrigger = ref(0); // Trigger for forcing badge reactivity

  /**
   * Show documentation dialog for a node (uses NEW category-based system)
   * @param {Object} node - Tree node with equipment data
   */
  const showDocumentationDialog = (node) => {
    categoryDocumentationEquipment.value = {
      id: node.data?.id,
      name: node.label,
      brick_class: node.brickClass || node.data?.brickClass,
      equipment_type: node.type || 'Equipment',
    };
    categoryDocumentationDialog.value = true;
  };

  /**
   * Show documentation dialog for an AHU (uses OLD system for backward compatibility)
   * @param {Object} node - AHU tree node
   */
  const documentAhu = (node) => {
    if (!node.data) {return;}

    documentationEquipment.value = {
      id: node.data.id,
      type: 'AHU',
      name: node.label,
      brickClass: node.data.brick_class,
    };
    documentationDialog.value = true;
  };

  /**
   * Document the currently selected AHU
   * Requires findAhuNode function from parent to locate the node
   * @param {string} selectedAhuId - The ID of the selected AHU
   */
  const documentSelectedAhu = (selectedAhuId) => {
    if (!findAhuNode) {
      if (notifyError) {
        notifyError('findAhuNode function not provided to composable');
      }
      return;
    }

    const ahuNode = findAhuNode(selectedAhuId);
    if (ahuNode) {
      documentAhu(ahuNode);
    }
  };

  /**
   * Handle equipment documentation saved event
   * Updates UI status and triggers refresh
   * @param {Object} documentation - Documentation data from save event
   */
  const onEquipmentDocumented = (documentation) => {
    // Update documentation status in the UI
    const key = `${documentation.brick_class || 'equipment'}-${documentation.equipment_id}`;
    documentationStatuses.value[key] = {
      status: documentation.documentation_status,
      percentage: documentation.completion_percentage || 0,
    };

    // Increment refresh trigger to force badge reactivity
    documentationRefreshTrigger.value++;

    // Refresh data if needed
    loadDocumentationStatuses();

    if (notifySuccess) {
      notifySuccess('Equipment documentation saved successfully');
    }
  };

  /**
   * Navigate to airflow tab with specific equipment selected
   * Closes documentation dialog and switches tabs
   * @param {Object} equipment - Equipment data with equipmentId and equipmentName
   */
  const navigateToAirflow = (equipment) => {
    // Close the documentation dialog
    documentationDialog.value = false;

    // Call navigation callback with airflow tab and equipment details
    if (onNavigate) {
      onNavigate({
        tab: 'airflow',
        ahuId: `ahu-${equipment.equipmentId}`,
        ahuName: equipment.equipmentName || 'Selected AHU',
        equipment,
      });
    }
  };

  /**
   * Load documentation statuses for all equipment from API
   * Updates documentationStatuses ref with current status data
   */
  const loadDocumentationStatuses = async () => {
    try {
      const response = await authenticatedFetch(`/api/sites/${siteId}/documentation-stats`, {
        headers: {
          'Accept': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success && data.data.equipment_statuses) {
        // Update documentation statuses for all equipment
        Object.entries(data.data.equipment_statuses).forEach(([key, statusData]) => {
          documentationStatuses.value[key] = {
            status: statusData.status,
            percentage: statusData.percentage,
          };
        });

        // Increment refresh trigger to force badge reactivity
        documentationRefreshTrigger.value++;
      }
    } catch (error) {
      // Silently fail - documentation status is not critical
      if (notifyError) {
        notifyError('Failed to load documentation statuses');
      }
    }
  };

  return {
    // State - Old documentation system (backward compatibility)
    documentationDialog,
    documentationEquipment,

    // State - New category-based documentation system
    categoryDocumentationDialog,
    categoryDocumentationEquipment,

    // Status tracking
    documentationStatuses,
    documentationRefreshTrigger,

    // Methods
    showDocumentationDialog,
    documentAhu,
    documentSelectedAhu,
    onEquipmentDocumented,
    navigateToAirflow,
    loadDocumentationStatuses,
  };
}
